import React, { useState, useEffect, useCallback } from 'react';
import { getBugReports, updateBugReportStatus } from '../api/apiClient';
import socket from '../socket';
import './AdminSettings.css';
import './AdminBugReports.css';

const relativeTime = (iso) => {
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const AdminBugReports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const fetchReports = useCallback(() => {
    getBugReports()
      .then(res => setReports(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchReports();
    socket.on('bugReports:changed', fetchReports);
    return () => socket.off('bugReports:changed', fetchReports);
  }, [fetchReports]);

  const toggleStatus = async (report) => {
    const next = report.status === 'open' ? 'resolved' : 'open';
    setBusyId(report.id);
    try {
      await updateBugReportStatus(report.id, next);
      setReports(prev => prev.map(r => r.id === report.id ? { ...r, status: next } : r));
    } catch {
      alert('Failed to update status');
    } finally {
      setBusyId(null);
    }
  };

  const openCount = reports.filter(r => r.status === 'open').length;

  return (
    <div>
      <div className="adm-search-bar">
        <span className="adm-count">{openCount} open / {reports.length} total</span>
      </div>

      <div className="adm-body">
        {loading ? (
          <p className="adm-loading">Loading…</p>
        ) : reports.length === 0 ? (
          <p className="adm-loading">No bug reports yet.</p>
        ) : (
          <ul className="bug-rep-list">
            {reports.map(r => {
              const name = r.reporter?.name || 'A former staff member';
              const initials = name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
              const busy = busyId === r.id;
              return (
                <li key={r.id} className={`bug-rep-row ${r.status === 'resolved' ? 'bug-rep-resolved' : ''}`}>
                  <div className="adm-avatar">
                    {r.reporter?.avatar
                      ? <img src={r.reporter.avatar} alt={name} />
                      : <span>{initials}</span>}
                  </div>
                  <div className="bug-rep-info">
                    <div className="bug-rep-top">
                      <span className="bug-rep-name">{name}</span>
                      <span className="bug-rep-time">{relativeTime(r.created_at)}</span>
                    </div>
                    <p className="bug-rep-desc">{r.description}</p>
                    {r.image_data && (
                      <a href={r.image_data} target="_blank" rel="noopener noreferrer" className="bug-rep-thumb-link">
                        <img src={r.image_data} alt="Screenshot" className="bug-rep-thumb" />
                      </a>
                    )}
                    {r.page_url && <span className="bug-rep-page">{r.page_url}</span>}
                  </div>
                  <button
                    className={`adm-btn ${r.status === 'open' ? 'adm-btn-admin promote' : 'adm-btn-cancel'}`}
                    onClick={() => toggleStatus(r)}
                    disabled={busy}
                  >
                    {busy ? '…' : r.status === 'open' ? 'Mark Resolved' : 'Reopen'}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AdminBugReports;
